import { getPhoneNumberForAPI } from "../PhoneInput/CustomPhoneInput";

/** Digits only, last 10 (strips a +1 prefix). Empty string when nothing usable. */
export const toTenDigitPhone = (value: string | null | undefined): string =>
  getPhoneNumberForAPI(value ?? "").slice(-10);

export const isValidTenDigitPhone = (value: string | null | undefined): boolean =>
  /^\d{10}$/.test(getPhoneNumberForAPI(value ?? ""));

/** The login session stores the mobile number base64-encoded; tolerate plain values too. */
export const decodeSessionPhone = (value: string | null | undefined): string => {
  if (!value) return "";
  // A plain number is also valid base64 characters, so check for it first or atob would mangle it.
  if (/^[\d\s()+.-]+$/.test(value)) return toTenDigitPhone(value);
  try {
    return toTenDigitPhone(atob(value));
  } catch {
    return toTenDigitPhone(value);
  }
};

/** Prepend https:// to scheme-less URLs; anything that isn't http(s) is rejected. */
export function toSafeHttpsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export type ConsentStatus = "in" | "out" | "none";

/** `User/SmsPreference` returns `optedIn: boolean` plus `smsStatus` (OPTED_IN / OPTED_OUT / PENDING); anything else is "not given". */
export const consentStatusOf = (pref: { optedIn?: boolean | null; smsStatus?: string | null } | null | undefined): ConsentStatus => {
  const status = pref?.smsStatus?.toUpperCase();
  if (status === "OPTED_IN") return "in";
  if (status === "OPTED_OUT") return "out";
  if (status === "NOT_GIVEN" || status === "NONE" || status === "PENDING") return "none";
  if (pref?.optedIn === true) return "in";
  return "none";
};

/**
 * Value to send as `isSmsConsent` when saving a user. Ticked opts in; unticked
 * only opts out someone who was opted in, otherwise the stored value is kept
 * (null = "not given" must not silently become "opted out").
 */
export const resolveSmsConsent = (ticked: boolean, stored: boolean | null): boolean | null =>
  ticked ? true : stored === true ? false : stored;

export const CONSENT_STATUS_CLASS: Record<ConsentStatus, string> = {
  in: "bg-green-50 text-green-700 border-green-200",
  none: "bg-gray-100 text-gray-500 border-gray-200",
  out: "bg-red-50 text-red-600 border-red-200",
};

export interface LegalDocument {
  code: "TERMS" | "PRIVACY";
  url: string | null;
}
